/**
 * @file recorderPriceHints.ts
 * @brief The quiet pricing lines in the meeting flow (calm recorder, DES
 *        19-10-2026), web and Mac only: a Free user's Meeting Notes minutes
 *        left, and the one-sentence rule before a Full Intelligence start
 *        that the allowance does not cover.
 *
 * @details
 * Read from `GET /billing/subscriptions` (the Billing page's own data), so
 * the numbers are the server's, never re-derived. The rules are ADR-045's:
 * - a plan holder past their Intelligence minutes: extra minutes go on the
 *   next invoice at the plan's overage rate;
 * - Free (B43, 31-10-2026, ADR-061): never charged. Full Intelligence comes
 *   out of the same free minutes as Meeting Notes, so there is no price line;
 *   used up = "You've used your free minutes.", Full capped by Super Admin
 *   and used = "Full Intelligence is on Pro. Pick a plan.";
 * - no plan while Free is off: pay as you go per meeting at the no-plan rate,
 *   with the minimum charge;
 * - a plan with plan overage off (`planOverage: false`): used up = the next
 *   Full Intelligence recording is refused, nothing extra is charged.
 * Company members get nothing here: their company's plan and admin decide.
 * The phone never shows these (companion billing, ADR-043).
 * Pure: no React, no network.
 *
 * Related: ./BoardroomRecorderPage.tsx, ./BoardroomSetupPage.tsx,
 *          ../my-billing/planCompare.ts (perMinute)
 *
 * @author DES
 * @date 20-10-2026
 */

import { formatMinor } from "../my-billing/planConfirm.ts";
import { perMinute } from "../my-billing/planCompare.ts";
import type { SubscriptionStatus } from "../my-billing/schemas.ts";
import { countryBlockedMessage } from "../my-billing/billingErrors.ts";

/**
 * "412 free minutes left", counting down while recording (B43: one pool for
 * Meeting Notes and Full Intelligence; a Full recording counts down what
 * Full may still use). A blocked country has no free minutes (B1): no line.
 */
export function freeNotesLeftLine(
  freePlan:
    | {
        notesRemaining: number;
        notAvailable?: { code: string } | null;
        intelRemainingMinutes?: number | null;
        resetMode?: string | null;
      }
    | null
    | undefined,
  elapsedMs = 0,
  tier: "notes" | "full" = "notes",
): string | null {
  if (!freePlan || freePlan.notAvailable || !Number.isFinite(freePlan.notesRemaining)) return null;
  const start = tier === "full" && freePlan.intelRemainingMinutes != null ? freePlan.intelRemainingMinutes : freePlan.notesRemaining;
  const left = Math.max(0, Math.floor(start - Math.max(0, elapsedMs) / 60_000));
  const month = freePlan.resetMode === "one_time" ? "" : " this month";
  return `${left.toLocaleString("en-GB")} free minute${left === 1 ? "" : "s"} left${month}`;
}

/**
 * One sentence before starting Full Intelligence, or null when the
 * allowance covers it (or it is not this user's money).
 */
export function fullIntelligenceNotice(status: SubscriptionStatus | null | undefined): string | null {
  if (!status || (status.billingScope && status.billingScope !== "personal")) return null;
  const currency = status.currency || status.basic?.currency || "GBP";
  const sub = status.subscription;

  // B43: Free is never charged; Full Intelligence uses the free minutes.
  const fm = status.freeMinutes;
  if (fm && !status.freePlan?.notAvailable) {
    if (fm.grantRefused) return fm.grantRefused.message;
    if ((fm.poolMinutesLeft ?? fm.remainingMinutes ?? 0) <= 0) return "You've used your free minutes.";
    if (fm.intelCapped && (fm.intelRemainingMinutes ?? 1) <= 0) return "Full Intelligence is on Pro. Pick a plan.";
    return null;
  }

  if (sub && sub.entitled !== false) {
    const pool = status.usage?.pools?.intelligence;
    const remaining = pool ? pool.remaining : status.usage?.remainingMinutes;
    if (remaining == null || remaining > 0) return null;
    // B43: plan overage off = no extra minutes; the start is refused.
    if (status.planOverage === false) {
      return "You've used this month's Meeting Intelligence minutes. They come back with your plan's next month.";
    }
    const plan = status.catalog.find((p) => p.code === sub.planCode);
    const rate = plan?.overageRateMinor;
    return rate != null
      ? `You've used this month's Full Intelligence minutes: extra minutes go on your next invoice at ${perMinute(rate, plan?.currency || currency)}.`
      : "You've used this month's Full Intelligence minutes: extra minutes go on your next invoice.";
  }

  // Blocked country (B1): pay as you go is refused too, so say that, not a rate.
  const blocked = countryBlockedMessage(status);
  if (blocked) return blocked;
  const rate = status.basic?.boardroomRateMinor;
  if (rate == null) return null;
  const min = status.catalog.find((p) => p.noPlanMinChargeMinor != null)?.noPlanMinChargeMinor;
  const minText = min ? ` (${formatMinor(min, currency)} minimum per meeting)` : "";
  return `Full Intelligence isn't included in your plan: it's charged pay as you go at ${perMinute(rate, currency)}${minText} to your card.`;
}
